import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("hastaan_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If we are returning from Emergent OAuth, AuthCallback will handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!cancelled) setUser(r.data);
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem("hastaan_token");
        setToken(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("hastaan_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem("hastaan_token", r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    // Best-effort GPS capture — silent if user denies
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          axios.post(`${API}/users/me/location`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude, address: null },
            { headers: { Authorization: `Bearer ${r.data.token}` } }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: false, timeout: 6000 },
      );
    }
    return r.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("hastaan_token");
    setToken(null);
    setUser(null);
  }, []);

  const authFetch = useCallback(
    (config) =>
      axios({ ...config, headers: { ...(config.headers || {}), Authorization: `Bearer ${token}` } }),
    [token]
  );

  const value = useMemo(
    () => ({ user, token, loading, login, register, logout, authFetch, API }),
    [user, token, loading, login, register, logout, authFetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
